import { useState, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { X, Upload, Camera } from "lucide-react";
import { toast } from "sonner";

interface ScreenshotUploadProps {
  screenshots: File[];
  autoCapture: Blob | null;
  onScreenshotsChange: (files: File[]) => void;
  onRemoveAutoCapture: () => void;
  maxFiles?: number;
}

export function ScreenshotUpload({
  screenshots,
  autoCapture,
  onScreenshotsChange,
  onRemoveAutoCapture,
  maxFiles = 3,
}: ScreenshotUploadProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [previewSrc, setPreviewSrc] = useState<string | null>(null);
  const autoCaptureUrl = autoCapture ? URL.createObjectURL(autoCapture) : null;

  const totalCount = screenshots.length + (autoCapture ? 1 : 0);
  const canAddMore = totalCount < maxFiles;

  const handleFiles = useCallback(
    (files: FileList | null) => {
      if (!files) return;
      const allowed = maxFiles - totalCount;
      if (allowed <= 0) {
        toast.error(`Maximum ${maxFiles} screenshots allowed`);
        return;
      }
      const newFiles = Array.from(files)
        .filter((f) => f.type.startsWith("image/"))
        .slice(0, allowed);
      if (newFiles.length === 0) {
        toast.error("Please select image files only");
        return;
      }
      onScreenshotsChange([...screenshots, ...newFiles]);
    },
    [screenshots, totalCount, maxFiles, onScreenshotsChange]
  );

  const removeFile = (index: number) => {
    onScreenshotsChange(screenshots.filter((_, i) => i !== index));
  };

  // Paste handler
  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      const imageFiles: File[] = [];
      for (const item of Array.from(items)) {
        if (item.type.startsWith("image/")) {
          const file = item.getAsFile();
          if (file) imageFiles.push(file);
        }
      }
      if (imageFiles.length > 0) {
        e.preventDefault();
        handleFiles(
          Object.assign(imageFiles, { item: (i: number) => imageFiles[i] }) as any
        );
      }
    },
    [handleFiles]
  );

  return (
    <div className="space-y-2" onPaste={handlePaste}>
      <label className="text-sm font-medium">
        Screenshots ({totalCount}/{maxFiles})
      </label>

      {/* Preview grid */}
      {(autoCapture || screenshots.length > 0) && (
        <div className="grid grid-cols-3 gap-2">
          {autoCaptureUrl && (
            <div className="relative rounded-md border overflow-hidden aspect-video bg-muted">
              <button
                type="button"
                onClick={() => setPreviewSrc(autoCaptureUrl)}
                className="w-full h-full cursor-pointer"
              >
                <img
                  src={autoCaptureUrl}
                  alt="Auto-captured screenshot"
                  className="w-full h-full object-cover"
                />
              </button>
              <div className="absolute top-1 left-1">
                <span className="text-[10px] bg-accent text-accent-foreground px-1.5 py-0.5 rounded">
                  Auto
                </span>
              </div>
              <button
                type="button"
                onClick={onRemoveAutoCapture}
                className="absolute top-1 right-1 p-0.5 rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          )}
          {screenshots.map((file, i) => {
            const objUrl = URL.createObjectURL(file);
            return (
              <div
                key={i}
                className="relative rounded-md border overflow-hidden aspect-video bg-muted"
              >
                <button
                  type="button"
                  onClick={() => setPreviewSrc(objUrl)}
                  className="w-full h-full cursor-pointer"
                >
                  <img
                    src={objUrl}
                    alt={`Screenshot ${i + 1}`}
                    className="w-full h-full object-cover"
                  />
                </button>
                <button
                  type="button"
                  onClick={() => removeFile(i)}
                  className="absolute top-1 right-1 p-0.5 rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Upload button */}
      {canAddMore && (
        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => handleFiles(e.target.files)}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload className="mr-2 h-3.5 w-3.5" />
            Add Screenshot
          </Button>
          <p className="text-xs text-muted-foreground mt-1">
            Or paste from clipboard (Ctrl+V)
          </p>
        </div>
      )}

      {/* Screenshot lightbox */}
      <Dialog open={!!previewSrc} onOpenChange={() => setPreviewSrc(null)}>
        <DialogContent className="max-w-4xl p-2">
          {previewSrc && (
            <img
              src={previewSrc}
              alt="Screenshot preview"
              className="w-full h-auto rounded"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
